import { _ as __rest } from './_tslib.js';
import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import { sortMatrixColumnByIndex, generateClasses } from '@ids/tools';
import { useState, useMemo } from 'react';
import { s as styleInject } from './style-inject.es.js';
import { I as IdsTableWrapper } from './table-wrapper.js';
import { I as IdsSort } from './sort.js';

/**
 * Hook para ordenação de colunas de uma matriz
 */
const useSortColumns = (table = []) => {
  const [sortedTable, setSortedTable] = useState(table);
  const [sortedIndex, setSortedIndex] = useState(-1);
  const [isAsc, setIsAsc] = useState(true);
  const sortByIndex = index => {
    const newColumns = sortMatrixColumnByIndex(sortedTable, index, isAsc);
    setSortedTable(newColumns);
    setSortedIndex(index);
    setIsAsc(!isAsc);
  };
  return {
    sortedTable,
    sortByIndex,
    sortedIndex,
    isAsc
  };
};

var css_248z = ".ids-table__footer-cell, .ids-table th:not(.ids-sort) {\n  color: var(--ids_color_text_heading_01);\n  font: var(--ids_textStyle_f02_s16_h24_wbd);\n  padding: 0 var(--ids_spacing_4x);\n}\n.ids-table__footer-cell:hover, .ids-table th:hover:not(.ids-sort) {\n  background-color: var(--ids_color_bg_variant_02);\n  color: var(--ids_color_text_heading_01);\n}\n.ids-table__footer-cell:active, .ids-table th:active:not(.ids-sort) {\n  background-color: var(--ids_color_action_neutral_base);\n  color: var(--ids_color_action_primary_contrast);\n}\n\n.ids-table td, .ids-table__footer-cell, .ids-table th:not(.ids-sort) {\n  background-color: var(--ids_color_bg_base);\n  -webkit-box-sizing: border-box;\n          box-sizing: border-box;\n  height: var(--ids_size_general_48x);\n  overflow: hidden;\n  text-align: center;\n  text-overflow: ellipsis;\n  vertical-align: middle;\n  white-space: nowrap;\n  width: var(--ids_size_general_64x);\n}\n\n.ids-table th:first-child:not(.ids-sort) {\n  border-top-left-radius: var(--ids_size_cornerRadius_tags_01);\n}\n.ids-table th:last-child:not(.ids-sort) {\n  border-top-right-radius: var(--ids_size_cornerRadius_tags_01);\n}\n\n.ids-table {\n  border: 0;\n  border-collapse: collapse;\n  border-spacing: 0;\n  width: 100%;\n}\n.ids-table th {\n  border-bottom: var(--ids_size_general_2x) solid var(--ids_color_border_medium);\n  height: var(--ids_size_general_48x);\n}\n.ids-table td {\n  color: var(--ids_color_text_body_02);\n  font: var(--ids_textStyle_f02_s16_h24_wrg);\n  padding: 0 var(--ids_spacing_6x);\n}\n.ids-table__footer-cell {\n  border-top: var(--ids_size_general_2x) solid var(--ids_color_border_medium);\n}\n.ids-table--solid-border td {\n  border: var(--ids_size_general_1x) solid var(--ids_color_border_medium);\n}\n.ids-table td:hover {\n  background-color: var(--ids_color_bg_variant_02);\n}\n.ids-table--zebra-style tr:nth-child(even) td:not(:hover) {\n  background-color: var(--ids_color_bg_variant_01);\n}\n\n.ids-table-sticky {\n  position: sticky;\n}\n\n.ids-table-sticky-border-elem-left {\n  border-right: var(--ids_size_general_1x) solid var(--ids_color_border_medium);\n}\n\n.ids-table-sticky-border-elem-right {\n  border-left: var(--ids_size_general_1x) solid var(--ids_color_border_medium);\n}\n\n.ids-table-wrapper {\n  overflow: auto;\n}";
styleInject(css_248z);

/**
 * @category Component
 *
 * @example
 * ```ts
 * import { IdsTable } from '@ids/react';
 * ```
 */
const IdsTable = _a => {
  var {
      borderStyle,
      rowStyle,
      children,
      className = '',
      columns = [],
      data = [],
      sort,
      wrapperProps
    } = _a,
    props = __rest(_a, ["borderStyle", "rowStyle", "children", "className", "columns", "data", "sort", "wrapperProps"]);
  const {
    sortedTable,
    sortByIndex,
    sortedIndex,
    isAsc
  } = useSortColumns(data);
  const classes = useMemo(() => generateClasses({
    'ids-table': true,
    'ids-table--solid-border': borderStyle === 'solid',
    'ids-table--zebra-style': rowStyle === 'zebra',
    [className]: className
  }), [borderStyle, className, rowStyle]);
  const template = useMemo(() => {
    if (!data.length) {
      return children;
    }
    const renderTH = (column, index) => {
      if (sort) {
        return jsx(IdsSort, Object.assign({
          onClick: () => sortByIndex(index),
          direction: sortedIndex === index ? isAsc ? 'asc' : 'desc' : undefined
        }, {
          children: column
        }), column);
      }
      return jsx("th", {
        children: column
      }, column);
    };
    return jsxs(Fragment, {
      children: [jsx("thead", {
        children: jsx("tr", {
          children: columns.map((column, index) => renderTH(column, index))
        })
      }), jsx("tbody", {
        children: sortedTable.map(row => jsx("tr", {
          children: columns.map((column, index) => jsx("td", {
            children: row[index]
          }, `${row[index]}-${column}`))
        }, row.join('-')))
      })]
    });
  }, [children, columns, data.length, isAsc, sort, sortByIndex, sortedIndex, sortedTable]);
  return jsx(IdsTableWrapper, Object.assign({}, wrapperProps, {
    children: jsx("table", Object.assign({}, props, {
      className: classes
    }, {
      children: template
    }))
  }));
};

export { IdsTable as I, useSortColumns as u };
//# sourceMappingURL=table.js.map
